import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import GameLayout from '../layout/GameLayout';
import FloatingCard from '../ui/FloatingCard';
import FloatingButton from '../ui/FloatingButton';
import ConfirmModal from '../ui/ConfirmModal';
import LeagueCreationForm from './LeagueCreationForm';
import LeagueEditForm from './LeagueEditForm';
import LeagueDetails from './LeagueDetails';
import tournamentService from '../../services/api/tournamentService';
import { apiClient } from '../../services/api/apiService';
import { canCreateCompetition, canManageLeague } from '../../utils/rbac';
import { useLeagueData } from './hooks/useLeagueData';
import { eid } from '../../utils/entityId';
import { formatDateOnly } from '../../utils/dateOnly';
import { getLeagueFormat } from './leagueFormat';
import { isFinalStatus, isRegistrationOpen, normalizeLeagueStatus } from './leagueStatus';
import EntityCard from '../ui/EntityCard';
import FilterBar from '../ui/FilterBar';
import { useUserFilters } from '../../contexts/UserFiltersContext';
import StatusTabs from '../ui/StatusTabs';
import { mineFirst } from '../../utils/listOrder';
import './leagues.css';
import '../../styles/features/teams.css';
import '../../styles/features/matches.css';
import '../../styles/features/tournaments.css';

const LEAGUE_STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'registration_open', label: 'Open' },
  { value: 'registration_closed', label: 'Closed' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const LEAGUE_TABS = ['overview', 'teams', 'schedule', 'matches', 'standings', 'messages'];

/**
 * Main Leagues Component
 * Manages league listing, creation, editing, and detailed viewing
 */
const Leagues = ({ token, user }) => {
  const navigate = useNavigate();
  // /leagues/:id — deep link / refresh opens that league directly (BUG-57)
  const { id: routeLeagueId } = useParams();
  // ?tab=messages (e.g. from a league chat notification) opens that tab
  const [searchParams] = useSearchParams();
  const requestedTab = LEAGUE_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : null;

  // Global filter context: sport types and location for server-side filtering
  const { userSports, selectedSportIds, sportTypeParams, filterParams, isFiltersReady, autoExpandRadius, needsLocation } = useUserFilters();
  const contextSportNames = userSports
    .filter((s) => selectedSportIds.includes(s.sport_id || s.id))
    .map((s) => s.name);

  // Build league-specific query params: sport_types (text) + optional location
  const leagueQueryParams = {
    ...sportTypeParams,
    ...(filterParams.lat != null ? {
      lat: filterParams.lat,
      lng: filterParams.lng,
      radius: filterParams.radius,
    } : {}),
  };

  const { leagues, loading, fetchLeagues, fetchLeagueById } = useLeagueData(leagueQueryParams, isFiltersReady, { skipList: needsLocation });

  // Auto-expand radius when proximity filter yields too few results
  useEffect(() => {
    if (!loading && filterParams.lat != null) {
      autoExpandRadius(leagues.length);
    }
  }, [leagues, loading, filterParams.lat, autoExpandRadius]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [activeTab, setActiveTab] = useState('teams');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingLeague, setEditingLeague] = useState(null);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [accessAction, setAccessAction] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    isDanger: false,
    isLoading: false,
    onConfirm: null,
  });

  // Search / filter state (client-side, for further narrowing within already-fetched results)
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sportFilter, setSportFilter] = useState('');

  // Create: league_admin / organization_admin / platform_admin / super_admin
  // (backend requireRole(['league_admin','organization_admin']) + admin bypass).
  const canCreateLeagues = canCreateCompetition(user);

  // Manage one league (edit / delete / status / schedule): only its creator or
  // a super/platform admin, as the backend checks. Being an admin role is not
  // enough on its own (BUG-31).
  const canManage = (league) => canManageLeague(user, league);

  // Register-team modal state
  const [registerModal, setRegisterModal] = useState({
    isOpen: false,
    league: null,
    myTeams: [],
    alreadyRegisteredTeam: null,
    isLeagueFull: false,
    loadingTeams: false,
    selectedTeamId: '',
    message: '',
    sending: false,
  });
  // Track which league IDs the current user already has a registered team in
  const [registeredLeagueIds, setRegisteredLeagueIds] = useState(new Set());

  // Id of the league currently shown (or being loaded) — see URL sync effect
  const loadedLeagueIdRef = useRef(null);

  /**
   * Handle league selection by ID
   */
  async function handleSelectLeague(leagueId) {
    const league = await fetchLeagueById(leagueId);
    if (league) {
      loadedLeagueIdRef.current = String(leagueId);
      setSelectedLeague(league);
      setActiveTab('teams');
      if (String(routeLeagueId) !== String(leagueId)) {
        navigate(`/leagues/${leagueId}`);
      }
    }
  }

  // Keep the selected league in sync with the URL: open the league on a
  // direct visit / refresh of /leagues/:id and close it on /leagues (e.g.
  // browser Back). loadedLeagueIdRef avoids refetching a league that was just
  // opened from the list.
  useEffect(() => {
    if (!routeLeagueId) {
      loadedLeagueIdRef.current = null;
      setSelectedLeague(null);
      return undefined;
    }
    if (loadedLeagueIdRef.current === String(routeLeagueId)) return undefined;

    let cancelled = false;
    let done = false;
    loadedLeagueIdRef.current = String(routeLeagueId);
    (async () => {
      const league = await fetchLeagueById(routeLeagueId);
      done = true;
      if (cancelled) return;
      if (league) {
        setSelectedLeague(league);
        setActiveTab('teams');
      } else {
        loadedLeagueIdRef.current = null;
        showFeedback('error', 'League not found or you do not have access to it.');
        navigate('/leagues', { replace: true });
      }
    })();
    return () => {
      cancelled = true;
      // Allow a re-run (e.g. React StrictMode) to fetch again
      if (!done) loadedLeagueIdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeLeagueId, fetchLeagueById, navigate]);

  /**
   * Handle league click from list
   */
  function handleLeagueClick(league) {
    if (!league?.id) {
      showFeedback('error', 'League data is incomplete.');
      return;
    }
    handleSelectLeague(eid(league) || league.id);
  }

  /**
   * Handle edit league
   */
  function handleEditLeague(league, event) {
    event.stopPropagation();

    if (!canManage(league)) {
      openAccessModal('edit leagues');
      return;
    }
    
    if (!league?.id) {
      showFeedback('error', 'Invalid league selected for editing.');
      return;
    }
    
    setEditingLeague(league);
    setShowEditForm(true);
  }

  /**
   * Handle delete league
   */
  async function handleDeleteLeague(league, event) {
    event.stopPropagation();

    if (!canManage(league)) {
      openAccessModal('delete leagues');
      return;
    }
    
    openConfirm({
      title: 'Delete League?',
      message: `Are you sure you want to delete "${league.name}"? This action cannot be undone.`,
      confirmText: 'Delete League',
      isDanger: true,
      onConfirm: async () => {
        try {
          await apiClient.delete(`/leagues/${eid(league) || league.id}`);
          showFeedback('success', 'League deleted successfully.');
          await fetchLeagues();
        } catch (error) {
          if (error.response?.status === 403) {
            openAccessModal('delete leagues');
            return;
          }
          const errorMsg = error.response?.data?.message || error.message;
          showFeedback('error', `Failed to delete league: ${errorMsg}`);
        }
      },
    });
  }

  /**
   * Open register-team modal and load the user's teams
   */
  async function handleOpenRegister(league, e) {
    e.stopPropagation();
    setRegisterModal({
      isOpen: true, league, myTeams: [], alreadyRegisteredTeam: null, loadingTeams: true, selectedTeamId: '', message: '', sending: false,
    });
    try {
      // Fetch both the user's teams and the league's enrolled teams in parallel
      const [teamsRes, enrolledRes] = await Promise.all([
        apiClient.get('/teams/my-teams'),
        apiClient.get(`/leagues/${eid(league) || league.id}/teams`),
      ]);

      const rawTeams = teamsRes.data?.data;
      const list = Array.isArray(rawTeams?.teams) ? rawTeams.teams : Array.isArray(rawTeams) ? rawTeams : [];

      // Only consider teams the user owns/created — not teams they're merely a member of
      const ownedTeams = list.filter((t) => t.created_by === user?.id || t.member_role === 'owner');

      // Filter by league sport type (case-insensitive)
      const leagueSport = (league?.sport_type || '').toLowerCase().trim();
      const sportMatching = leagueSport
        ? ownedTeams.filter((t) => (t.sport_type || t.sport || '').toLowerCase().trim() === leagueSport)
        : ownedTeams;

      const rawEnrolled = enrolledRes.data?.data;
      const enrolledList = Array.isArray(rawEnrolled) ? rawEnrolled : Array.isArray(rawEnrolled?.teams) ? rawEnrolled.teams : [];
      const alreadyIn = new Set(enrolledList.map((t) => t.team_id ?? t.id));

      const isLeagueFull = league?.total_teams != null && enrolledList.length >= league.total_teams;

      const alreadyRegisteredTeam = sportMatching.find((t) => alreadyIn.has(t.id)) || null;
      const eligible = sportMatching.filter((t) => !alreadyIn.has(t.id));

      if (alreadyRegisteredTeam) {
        setRegisteredLeagueIds((prev) => new Set([...prev, league.id]));
      }

      setRegisterModal((prev) => ({ ...prev, myTeams: eligible, alreadyRegisteredTeam, isLeagueFull, loadingTeams: false }));
    } catch {
      setRegisterModal((prev) => ({ ...prev, loadingTeams: false }));
    }
  }

  /**
   * Submit join request to the league host
   */
  async function handleSubmitRegister() {
    if (!registerModal.selectedTeamId) return;
    setRegisterModal((prev) => ({ ...prev, sending: true }));
    try {
      await tournamentService.requestToJoinLeague(
        registerModal.league.id,
        parseInt(registerModal.selectedTeamId, 10),
        registerModal.message,
      );
      showFeedback('success', '📩 Registration request sent! The host will review your request.');
      setRegisterModal({ isOpen: false, league: null, myTeams: [], alreadyRegisteredTeam: null, isLeagueFull: false, loadingTeams: false, selectedTeamId: '', message: '', sending: false });
    } catch (err) {
      showFeedback('error', err.message || 'Failed to send registration request');
      setRegisterModal((prev) => ({ ...prev, sending: false }));
    }
  }

  /**
   * Render register-team modal
   */
  function renderRegisterModal() {
    if (!registerModal.isOpen) return null;
    const { league, myTeams, alreadyRegisteredTeam, isLeagueFull, loadingTeams, selectedTeamId, message, sending } = registerModal;
    const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
    const cardStyle = { background: '#0f172a', border: '1px solid rgba(99,102,241,0.4)', borderRadius: '0.75rem', padding: '1.5rem', width: '100%', maxWidth: '420px', color: '#e2e8f0' };
    const inputStyle = { width: '100%', padding: '0.55rem 0.85rem', background: 'rgba(15,23,42,0.7)', border: '1px solid rgba(99,102,241,0.4)', borderRadius: '0.4rem', color: '#e2e8f0', fontSize: '0.9rem', boxSizing: 'border-box', marginBottom: '0.75rem' };

    return (
      <div style={overlayStyle} onClick={() => setRegisterModal((prev) => ({ ...prev, isOpen: false }))}>
        <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, color: '#a78bfa', fontSize: '1rem' }}>📩 Register for {league?.name}</h3>
            <button onClick={() => setRegisterModal((prev) => ({ ...prev, isOpen: false }))} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '1.2rem', cursor: 'pointer' }}>×</button>
          </div>
          {loadingTeams ? (
            <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Loading your teams…</p>
          ) : isLeagueFull ? (
            /* League has no more spots */
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.65rem 0.85rem', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '0.4rem', fontSize: '0.87rem', color: '#fca5a5' }}>
              <span style={{ fontSize: '1rem', flexShrink: 0 }}>🚫</span>
              <span>This league is full ({league?.total_teams}/{league?.total_teams} teams). Registration is no longer available.</span>
            </div>
          ) : alreadyRegisteredTeam ? (
            /* User already has an owned team enrolled in this league */
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.65rem 0.85rem', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '0.4rem', fontSize: '0.87rem', color: '#86efac' }}>
              <span style={{ fontSize: '1rem', flexShrink: 0 }}>✅</span>
              <span>
                <strong>{alreadyRegisteredTeam.name}</strong> is already registered in this league.
                {' '}Only one team per league is allowed.
              </span>
            </div>
          ) : myTeams.length === 0 ? (
            <p style={{ color: '#64748b', fontSize: '0.9rem' }}>
              {league?.sport_type
                ? `No eligible ${league.sport_type} teams. Create a ${league.sport_type} team first.`
                : 'You have no teams to register with. Create a team first.'}
            </p>
          ) : (
            <>
              <label style={{ fontSize: '0.82rem', color: '#94a3b8', display: 'block', marginBottom: '0.3rem' }}>Select your team</label>
              <select
                value={selectedTeamId}
                onChange={(e) => setRegisterModal((prev) => ({ ...prev, selectedTeamId: e.target.value }))}
                style={inputStyle}
              >
                <option value="">Choose a team…</option>
                {myTeams.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <label style={{ fontSize: '0.82rem', color: '#94a3b8', display: 'block', marginBottom: '0.3rem' }}>Message to host (optional)</label>
              <input
                type="text"
                placeholder="Introduce your team or add a note…"
                value={message}
                onChange={(e) => setRegisterModal((prev) => ({ ...prev, message: e.target.value }))}
                maxLength={200}
                style={inputStyle}
              />
              <div style={{ textAlign: 'right', fontSize: '0.72rem', marginTop: '-0.55rem', marginBottom: '0.5rem', color: message.length > 160 ? '#f59e0b' : '#475569' }}>
                {message.length}/200
              </div>
              <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', marginTop: '0.25rem' }}>
                <button
                  onClick={() => setRegisterModal((prev) => ({ ...prev, isOpen: false }))}
                  style={{ padding: '0.4rem 1rem', background: 'transparent', border: '1px solid rgba(100,116,139,0.4)', color: '#94a3b8', borderRadius: '0.4rem', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitRegister}
                  disabled={sending || !selectedTeamId}
                  style={{ padding: '0.4rem 1.1rem', background: sending ? 'rgba(99,102,241,0.3)' : 'rgba(99,102,241,0.8)', border: '1px solid rgba(99,102,241,0.6)', color: '#fff', borderRadius: '0.4rem', cursor: sending || !selectedTeamId ? 'not-allowed' : 'pointer', fontWeight: 600 }}
                >
                  {sending ? 'Sending…' : '📩 Send Request'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  /**
   * Open access modal
   */
  function openAccessModal(action) {
    setAccessAction(action);
    setShowAccessModal(true);
  }

  function showFeedback(type, message) {
    if (!message) {
      setFeedback(null);
      return;
    }
    setFeedback({ type, message });
    window.clearTimeout(showFeedback.timeoutId);
    showFeedback.timeoutId = window.setTimeout(() => setFeedback(null), 4000);
  }

  function clearFeedback() {
    setFeedback(null);
  }

  function openConfirm({ title, message, confirmText, isDanger, onConfirm }) {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      confirmText: confirmText || 'Confirm',
      isDanger: Boolean(isDanger),
      isLoading: false,
      onConfirm,
    });
  }

  function closeConfirm() {
    setConfirmModal((prev) => ({ ...prev, isOpen: false, isLoading: false }));
  }

  /**
   * Utility function: Format date
   */
  function formatDate(dateString) {
    // League dates are calendar dates (DATE columns) — never shift by timezone.
    return formatDateOnly(dateString, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }, '');
  }

  /**
   * Utility function: Format time
   */
  function formatTime(timeString) {
    return new Date(`1970-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  /**
   * Render access modal
   */
  function renderAccessModal() {
    if (!showAccessModal) return null;

    return (
      <div className="modal-overlay-gaming" role="dialog" aria-modal="true">
        <FloatingCard size="large" transparent={false} hover={false} className="modal-gaming">
          <div className="modal-header">
            <h3>Access Restricted</h3>
            <button
              className="close-button"
              onClick={() => setShowAccessModal(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="modal-body">
            <p>You do not have permission to {accessAction}. Request access from your organization admin or upgrade your role.</p>
          </div>
          <div className="modal-actions">
            <FloatingButton variant="secondary" size="small" onClick={() => setShowAccessModal(false)}>
              Close
            </FloatingButton>
            <FloatingButton
              variant="primary"
              size="small"
              onClick={() => {
                setShowAccessModal(false);
                navigate('/profile');
              }}
            >
              Go to Profile
            </FloatingButton>
          </div>
        </FloatingCard>
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <GameLayout token={token} onLogout={() => {}}>
        <div className="leagues-gaming-layout">
          <FloatingCard variant="primary" size="large" className="delay-1">
            <div className="loading-state-gaming">
              <div className="loading-spinner-gaming"></div>
              <p className="loading-text-gaming">Loading leagues...</p>
            </div>
          </FloatingCard>
        </div>
      </GameLayout>
    );
  }

  // Show creation form
  if (showCreateForm) {
    return (
      <LeagueCreationForm
        sports={userSports}
        token={token}
        onCancel={() => setShowCreateForm(false)}
        onSuccess={(message) => {
          setShowCreateForm(false);
          fetchLeagues();
          if (message) showFeedback('success', message);
        }}
      />
    );
  }

  // Show edit form
  if (showEditForm && editingLeague) {
    return (
      <LeagueEditForm
        league={editingLeague}
        sports={userSports}
        token={token}
        openAccessModal={openAccessModal}
        onCancel={() => {
          setShowEditForm(false);
          setEditingLeague(null);
        }}
        onSuccess={(message) => {
          setShowEditForm(false);
          setEditingLeague(null);
          fetchLeagues();
          if (message) showFeedback('success', message);
        }}
      />
    );
  }

  // Show league details
  if (selectedLeague) {
    return (
      <>
        <LeagueDetails
          league={selectedLeague}
          token={token}
          user={user}
          initialTab={requestedTab || activeTab}
          canManageLeagues={canManage(selectedLeague)}
          showFeedback={showFeedback}
          feedback={feedback}
          clearFeedback={clearFeedback}
          formatDate={formatDate}
          formatTime={formatTime}
          openConfirm={openConfirm}
          onBack={() => {
            setSelectedLeague(null);
            setActiveTab('teams');
            navigate('/leagues');
          }}
        />
        {renderAccessModal()}
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText={confirmModal.confirmText}
          onConfirm={async () => {
            if (!confirmModal.onConfirm) return;
            setConfirmModal((prev) => ({ ...prev, isLoading: true }));
            await confirmModal.onConfirm();
            closeConfirm();
          }}
          onCancel={closeConfirm}
          isLoading={confirmModal.isLoading}
          isDanger={confirmModal.isDanger}
        />
      </>
    );
  }

  // Show leagues list
  return (
    <GameLayout token={token} onLogout={() => {}}>
      <div className="leagues-gaming-layout">
        {feedback && (
          <div className={`league-feedback ${feedback.type}`}>
            <span>{feedback.message}</span>
            <button
              type="button"
              className="league-feedback-close"
              onClick={clearFeedback}
              aria-label="Dismiss message"
            >
              ×
            </button>
          </div>
        )}
        {/* Header */}
        <div className="leagues-header-gaming">
          <FloatingCard variant="primary" size="large" className="delay-1">
            <div className="header-content-gaming">
              <div className="title-section-gaming">
                <h1 className="leagues-title-gaming">
                  <span className="title-icon-gaming">🏟️</span>
                  League Arena
                </h1>
                <p className="leagues-subtitle-gaming">
                  Manage season-long competitions and championship leagues
                </p>
              </div>

              <div className="header-actions-gaming">
                <FloatingButton
                  variant="secondary"
                  size="medium"
                  onClick={() => navigate('/dashboard')}
                  icon="←"
                >
                  <span className="btn-text">Back</span>
                </FloatingButton>
                <FloatingButton
                  variant="success"
                  size="large"
                  onClick={() => {
                    if (!canCreateLeagues) {
                      openAccessModal('create leagues');
                      return;
                    }
                    setShowCreateForm(true);
                  }}
                  icon="➕"
                  className="delay-2"
                >
                  Create League
                </FloatingButton>
              </div>
            </div>
          </FloatingCard>
        </div>

        {/* Filter Bar */}
        <div className="leagues-filters-gaming">
          <FloatingCard variant="outline" size="medium" className="delay-3">
            <FilterBar inline showSports={false} />
            <div className="filters-content-gaming">
              <StatusTabs
                tabs={LEAGUE_STATUS_TABS}
                value={statusFilter}
                onChange={setStatusFilter}
                label="League status"
              />
              <div className="filters-grid-gaming">
                <div className="filter-group-gaming">
                  <label>🔎 Search</label>
                  <input
                    type="text"
                    placeholder="Search leagues…"
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    className="gaming-input"
                  />
                </div>
                {userSports.length > 0 && (
                  <div className="filter-group-gaming">
                    <label>🏆 Sport</label>
                    <select
                      value={sportFilter}
                      onChange={(e) => setSportFilter(e.target.value)}
                      className="gaming-select"
                    >
                      <option value="">All Sports</option>
                      {userSports.map((s) => (
                        <option key={s.sport_id || s.id} value={s.name}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {(searchFilter || statusFilter || sportFilter) && (
                  <div className="filter-actions-gaming">
                    <FloatingButton
                      variant="warning"
                      size="small"
                      onClick={() => { setSearchFilter(''); setStatusFilter(''); setSportFilter(''); }}
                      icon="🧹"
                    >
                      <span className="btn-text">Clear Filters</span>
                    </FloatingButton>
                  </div>
                )}
              </div>
            </div>
          </FloatingCard>
        </div>

        {/* Content */}
        {(() => {
          const filteredLeagues = leagues.filter((league) => {
            const q = searchFilter.toLowerCase();
            const matchesSearch = !q ||
              league.name?.toLowerCase().includes(q) ||
              league.venue_name?.toLowerCase().includes(q) ||
              league.sport_type?.toLowerCase().includes(q);
            // "active" is the legacy name of in_progress (the season is running)
            const matchesStatus = !statusFilter || normalizeLeagueStatus(league.status) === statusFilter;
            // If user has specific sports selected in the global context, filter by those;
            // local manual override (sportFilter) takes full precedence when set.
            const matchesSport = sportFilter
              ? league.sport_type?.toLowerCase() === sportFilter.toLowerCase()
              : contextSportNames.length === 0 || contextSportNames.some(
                  (n) => league.sport_type?.toLowerCase() === n.toLowerCase()
                );
            return matchesSearch && matchesStatus && matchesSport;
          });
          // Leagues I play in or host come first
          const mineIds = new Set(leagues.filter((l) => l.viewer_entered || l.viewer_hosts).map((l) => l.id));
          const shownLeagues = mineFirst(filteredLeagues, mineIds);

          if (filteredLeagues.length === 0) return (
          <FloatingCard variant="secondary" size="large" className="delay-3" key="empty">
            <div className="empty-state-gaming">
              <div className="empty-icon-gaming">
                <span style={{fontSize: '4rem'}}>🏟️</span>
              </div>
              <h3 className="empty-title-gaming">
                {needsLocation ? 'Leagues near you' : leagues.length === 0 ? 'No Leagues Found' : 'No matches for your filters'}
              </h3>
              <p className="empty-description-gaming">
                {needsLocation
                  ? "Set your location above to see what's near you."
                  : leagues.length === 0
                  ? 'Create your first league to get started with organized competition management.'
                  : 'Try adjusting your search or clearing filters.'}
              </p>
              {leagues.length === 0 && !needsLocation && (
                <FloatingButton
                  variant="primary"
                  size="medium"
                  onClick={() => {
                    if (canCreateLeagues) {
                      setShowCreateForm(true);
                    } else {
                      openAccessModal('create leagues');
                    }
                  }}
                  icon="➕"
                  className="delay-4"
                >
                  Create Your First League
                </FloatingButton>
              )}
            </div>
          </FloatingCard>
          );

          return (
          <div className="leagues-grid-gaming" key="grid">
            {shownLeagues.map((league) => {
              const format = getLeagueFormat(league);
              const manager = canManage(league);
              const nights = Array.isArray(league.game_days) && league.game_days.length > 0
                ? league.game_days.join(', ')
                : league.game_day ? `${league.game_day}s` : 'TBD';
              const courts = Number(league.number_of_courts) || 0;
              const host = league.created_by_name || league.created_by_username;
              const season = league.season || `${formatDate(league.season_start_date)} – ${formatDate(league.season_end_date)}`;
              return (
                <EntityCard
                  key={league.id}
                  className="league-list-card"
                  title={league.name}
                  badges={[
                    { status: league.status },
                    league.viewer_hosts ? { label: '⭐ Hosting', tone: 'success' }
                      : league.viewer_entered ? { label: '⭐ Playing', tone: 'success' } : null,
                    {
                      label: [league.sport_type, format.isSingles && 'Singles', format.isDoubles && 'Doubles', season]
                        .filter(Boolean).join(' · '),
                      tone: 'sport',
                    },
                  ].filter(Boolean)}
                  rows={[
                    { key: 'nights', icon: '📅', label: 'Nights', value: `${nights} · ${formatTime(league.start_time)} – ${formatTime(league.end_time)}` },
                    { key: 'venue', icon: '📍', label: 'Venue', value: `${league.venue_name || 'Venue TBD'}${courts ? ` · ${courts} court${courts > 1 ? 's' : ''}` : ''}` },
                    { key: 'entries', icon: '👥', label: format.unitPlural, value: `${league.registered_teams || 0}/${league.total_teams}` },
                    host && {
                      key: 'host',
                      icon: '🏆',
                      label: 'Host',
                      value: (
                        <button
                          type="button"
                          className="league-host-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            const profileId = league.created_by_short_code || league.created_by_uuid || league.created_by;
                            if (profileId) navigate(`/players/${profileId}`);
                          }}
                        >
                          {host}
                        </button>
                      ),
                    },
                  ].filter(Boolean)}
                  actions={(
                    <>
                      {/* Status changes live on the league page; Edit until the league is over */}
                      {manager && !isFinalStatus(league.status) && (
                        <FloatingButton variant="success" size="small" onClick={(e) => handleEditLeague(league, e)}>
                          ✏️ Edit
                        </FloatingButton>
                      )}
                      {/* A league with played matches is cancelled, never deleted (LG-B09) */}
                      {manager && !(Number(league.played_matches) > 0) && (
                        <FloatingButton variant="danger" size="small" onClick={(e) => handleDeleteLeague(league, e)}>
                          🗑️ Delete
                        </FloatingButton>
                      )}
                      {/* Register Team: non-host, public, active league */}
                      {!manager && isRegistrationOpen(league) && league.is_public !== false && (
                        format.isTeam ? (
                          <FloatingButton
                            variant={registeredLeagueIds.has(league.id) ? 'outline' : 'primary'}
                            size="small"
                            disabled={registeredLeagueIds.has(league.id)}
                            onClick={(e) => handleOpenRegister(league, e)}
                          >
                            {registeredLeagueIds.has(league.id) ? '✅ Registered' : '📩 Register Team'}
                          </FloatingButton>
                        ) : (
                          /* Singles / doubles: players join from the league's Teams tab */
                          <FloatingButton
                            variant="primary"
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleLeagueClick(league); }}
                          >
                            {format.isSingles ? '🙋 Join as Player' : '👥 Join as Pair'}
                          </FloatingButton>
                        )
                      )}
                    </>
                  )}
                  onClick={() => handleLeagueClick(league)}
                />
              );
            })}
          </div>
          );
        })()}
      </div>
      {renderAccessModal()}
      {renderRegisterModal()}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        onConfirm={async () => {
          if (!confirmModal.onConfirm) return;
          setConfirmModal((prev) => ({ ...prev, isLoading: true }));
          await confirmModal.onConfirm();
          closeConfirm();
        }}
        onCancel={closeConfirm}
        isLoading={confirmModal.isLoading}
        isDanger={confirmModal.isDanger}
      />
    </GameLayout>
  );
};

export default Leagues;
